import React, { ChangeEvent, FC, useRef } from 'react';

import { useDialog } from 'hooks/useDialog';

import { RaaDialog } from 'raa-react';

import { DokumentationstypEnum } from 'enums';

import { parse } from 'exif-date';
import EXIF from 'exif-js';
import { getImageDimensions } from 'utilities/imageUtilities';

export enum ValidFileMimeTypesEnum {
  IMAGE_JPEG = 'image/jpeg',
  IMAGE_PNG = 'image/png'
}

interface ImageFileUploadProps {
  uploading: () => void;
  success: (file: File, height: number, width: number, date?: Date) => void;
  error?: (message: string) => void;
  name: string;
  displayAsButton?: boolean;
  labelText: string;
  validFileMimeTypes: Array<ValidFileMimeTypesEnum>;
  dokumentationstyp: DokumentationstypEnum;
}

export const ImageFileUploadInput: FC<ImageFileUploadProps> = ({
  uploading,
  success,
  error,
  name,
  displayAsButton,
  labelText,
  validFileMimeTypes,
  dokumentationstyp
}) => {
  const { setDialog, unsetDialog } = useDialog();
  const fileUploadField = useRef<HTMLInputElement>(null);
  const maxFileSize = 1024 * 1024 * 15;
  const minImageWidth = 600;
  const minImageHeight = 600;
  const maxFileNameLength = 62;

  const clearTarget = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    event.currentTarget.value = '';
  };

  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length === 1) {
      const file = event.target.files[0];

      if (file.size > maxFileSize) {
        fileUploadError(
          `Bildens storlek får vara max 15 MB. Den valda filen är ${(file.size / (1024 * 1024)).toFixed(1)} MB.`
        );
        return;
      }

      if (file.name.length > maxFileNameLength) {
        fileUploadError(
          `Bildfilens namn får vara max ${maxFileNameLength} tecken. Den valda filens namn är ${file.name.length} tecken.`
        );
        return;
      }

      if (!validFileMimeTypes.includes(file.type as ValidFileMimeTypesEnum)) {
        const validFormats = validFileMimeTypes.map((type) => {
          if (type === ValidFileMimeTypesEnum.IMAGE_JPEG) return '.jpg .jpeg';
          if (type === ValidFileMimeTypesEnum.IMAGE_PNG) return '.png';
        });

        fileUploadError(`Ogiltigt filformat. Tillåtna filformat är ${validFormats.join(' ')}.`);
        return;
      }

      const dimensions = await getImageDimensions(file);
      if (!dimensions) {
        fileUploadError('Se till att filen verkligen innehåller en bild.');
        return;
      }

      if (dimensions.width < minImageWidth) {
        fileUploadError(`Bilden måste vara minst 600 px bred. Den valda bilden är ${dimensions.width} px bred.`);
        return;
      }

      if (dimensions.height < minImageHeight) {
        fileUploadError(`Bilden måste vara minst 600 px hög. Den valda bilden är ${dimensions.height} px hög.`);
        return;
      }

      uploading();

      const buffer = await file.arrayBuffer();
      const result = EXIF.readFromBinaryFile(buffer);
      let date: Date = new Date();
      if (result?.DateTimeDigitized) {
        date = parse(result?.DateTimeDigitized);
      }

      success(file, dimensions.height, dimensions.width, date);
    }
  };

  const fileUploadError = (message: string) => {
    error && error(message);
    setDialog(RaaDialog, {
      header: 'Filen kan inte användas',
      proceed: () => {
        unsetDialog();
        fileUploadField.current?.click();
      },
      proceedButtonText: 'Byt bildfil',
      cancel: unsetDialog,
      cancelButtonText: 'Avbryt',
      ariaDescribedBy: 'dialog-description',
      children: (
        <p id="dialog-description" className="tw-my-4">
          {message}
        </p>
      )
    });
  };

  return (
    <>
      <label
        htmlFor={name}
        className={
          displayAsButton
            ? 'raa-button raa-button-secondary tw-py-2'
            : 'tw-group tw-inline-block tw-items-center tw-gap-1 tw-rounded tw-px-1 tw-text-raa-orange focus-within:tw-bg-raa-orange focus-within:tw-text-raa-white focus-within:tw-outline-transparent'
        }
      >
        {!displayAsButton && <span className="raa-icon raa-icon-add tw-mr-1 !tw-no-underline" />}
        <span
          role="button"
          className="tw-break-word tw-text-left focus:tw-outline-transparent group-hover:tw-underline"
          aria-controls={name}
          tabIndex={0}
        >
          {labelText}
        </span>
      </label>
      <input
        id={name}
        name={name}
        data-testid={dokumentationstyp === DokumentationstypEnum.BILD ? 'imageFileUpload' : 'ritningFileUpload'}
        type="file"
        accept=".jpg,.jpeg,.png"
        className="tw-hidden"
        onChange={onFileChange}
        onClick={clearTarget}
        ref={fileUploadField}
      />
    </>
  );
};

export default ImageFileUploadInput;
